import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

function resolveImageUrl(url) {
  if (!url || typeof url !== 'string' || url.trim() === '') return '/placeholder-image.png';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${path}`;
}

function formatDateTime(dateString) {
  const d = new Date(dateString);
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const time = `${hours}:${minutes}${ampm}`;
  const date = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  return `${time} - ${date}`;
}

const ProductDetail = () => {
  const { id, branch } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useContext(AuthContext);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id, branch]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/products/${branch}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setProduct(response.data.product);
        setQuantity(response.data.product.quantity);
      } else {
        setError('Failed to fetch product details');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Failed to fetch product details');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityUpdate = async () => {
    try {
      setUpdateSuccess(false);
      const token = localStorage.getItem('token');
      const response = await axios.patch(`/api/products/${branch}/${id}/quantity`, 
        { quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setUpdateSuccess(true);
        setProduct({ ...product, quantity });
        setTimeout(() => setUpdateSuccess(false), 3000);
      } else {
        setError('Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError('Failed to update quantity');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/products/${branch}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        alert('Product deleted successfully');
        navigate(`/${branch}`);
      } else {
        setError('Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      setError('Failed to delete product');
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading product details...</div>;
  }

  if (error || !product) {
    return (
      <div className="alert alert-danger" role="alert">
        {error || 'Product not found'}
        <button className="btn btn-primary ms-3" onClick={() => navigate('/')}>
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h2 className="mb-0">Product Details</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to Products
        </button>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-4 mb-4">
            <img 
              src={resolveImageUrl(product.image_url)} 
              className="img-fluid rounded" 
              alt={product.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-image.png';
              }}
            />
          </div>
          <div className="col-md-8">
            <h3>{product.name}</h3>
            <p className="mb-3"><strong>Item Code:</strong> {product.item_code}</p>
            
            <div className="mb-4">
              <label htmlFor="quantity" className="form-label">Quantity</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                  min="0"
                />
                <button 
                  className="btn btn-primary"
                  onClick={handleQuantityUpdate}
                >
                  Update Quantity
                </button>
              </div>
              {updateSuccess && (
                <div className="alert alert-success mt-2">
                  Quantity updated successfully!
                </div>
              )}
            </div>
            
            {isAdmin() && (
              <div>
                {product.last_stock_update && (
                  <div className="text-muted small mb-2">
                    Updated: {formatDateTime(product.last_stock_update)}
                  </div>
                )}
                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-warning" onClick={() => navigate(`/${branch}/edit-product/${id}`)}>
                    Edit Product
                  </button>
                  <button className="btn btn-danger" onClick={handleDelete}>
                    Delete Product
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
